import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-scroll';
import { FiMoon, FiSun, FiHome, FiUser, FiCode, FiFolder, FiBriefcase, FiMail, FiChevronRight } from 'react-icons/fi';

function Navbar({ toggleMode, mode }) {
    const [expanded, setExpanded] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 1920 : true));
    const [brandIsIcon, setBrandIsIcon] = useState(false);

    const currentActive = useRef(null);
    const isManualClick = useRef(false);
    const clickTimeout = useRef(null);

    const setActive = (id) => {
        if (currentActive.current === id) return;
        currentActive.current = id;
        document.querySelectorAll('.side-nav__link.active').forEach(el => el.classList.remove('active'));
        const link = document.querySelector(`.side-nav__link[data-target="${id}"]`);
        if (link) link.classList.add('active');
    };

    useEffect(() => {
        const syncExpandedState = () => {
            if (window.innerWidth >= 1920) {
                setExpanded(true);
            } else {
                setExpanded(false);
            }
        };

        syncExpandedState();
        window.addEventListener('resize', syncExpandedState);
        return () => window.removeEventListener('resize', syncExpandedState);
    }, []);

    useEffect(() => {
        if (expanded) {
            setBrandIsIcon(false);
            return undefined;
        }

        setBrandIsIcon(false);
        const interval = window.setInterval(() => {
            setBrandIsIcon(prev => !prev);
        }, 1600);

        return () => window.clearInterval(interval);
    }, [expanded]);

    const links = [
        { label: 'Home', target: 'home', icon: <FiHome size={18} /> },
        { label: 'About', target: 'about', icon: <FiUser size={18} /> },
        { label: 'Skills', target: 'skills', icon: <FiCode size={18} /> },
        { label: 'Projects', target: 'projects', icon: <FiFolder size={18} /> },
        { label: 'Experience', target: 'experience', icon: <FiBriefcase size={18} /> },
        { label: 'Contact', target: 'contact', icon: <FiMail size={18} /> },
    ];

    useEffect(() => {
        const sectionIds = ['home', 'about', 'skills', 'projects', 'experience', 'contact'];
        const sections = sectionIds.map(id => document.getElementById(id)).filter(Boolean);
        if (!sections.length) return undefined;

        const observerCallback = (entries) => {
            // Stop ALL updates during click animation
            if (isManualClick.current) return;

            // Filter sections currently visible in our middle detection band
            const visible = entries.filter(e => e.isIntersecting);

            if (visible.length > 0) {
                // Pick the section that has the largest visible area
                visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
                setActive(visible[0].target.id);
            }
        };

        // Window band: 30% from top, 50% from bottom gives a stable single focal line
        const observer = new IntersectionObserver(observerCallback, {
            root: null,
            rootMargin: '-30% 0px -50% 0px',
            threshold: [0.15, 0.4, 0.7]
        });

        sections.forEach(s => observer.observe(s));

        const handleScroll = () => {
            // Don't calculate scroll positioning while smooth-scrolling from click
            if (isManualClick.current) return;

            // Handle edge case for top of page (Home)
            if (window.scrollY < 100) {
                setActive('home');
                return;
            }

            // Handle edge case for bottom of page (Contact)
            const isBottom = (window.innerHeight + window.scrollY) >= (document.documentElement.scrollHeight - 60);
            if (isBottom) {
                setActive('contact');
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        // Initial setup check
        handleScroll();

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', handleScroll);
            if (clickTimeout.current) clearTimeout(clickTimeout.current);
        };
    }, []);

    const handleNavClick = (target) => {
        // 1. Immediately highlight clicked section
        setActive(target);

        // 2. Lock observer/scroll listeners completely
        isManualClick.current = true;

        if (clickTimeout.current) clearTimeout(clickTimeout.current);

        // 3. Keep locked for 700ms (covers duration + layout settle)
        clickTimeout.current = setTimeout(() => {
            isManualClick.current = false;
        }, 750);

        if (window.innerWidth < 1920) {
            setExpanded(false);
        }
    };

    return (
        <aside className={`side-nav ${mode ? 'side-nav-dark' : 'side-nav-light'} ${expanded ? 'side-nav--expanded' : ''}`}>
            <div className="side-nav__inner">
                <button type="button" className="side-nav__brand" onClick={() => setExpanded(prev => !prev)} aria-label="Toggle navigation">
                    <span className="side-nav__brand-mark">
                        {!expanded && (brandIsIcon ? <FiChevronRight size={20} /> : <span>B</span>)}
                    </span>
                    <span className="side-nav__brand-name">Basharat Ali Sahito</span>
                </button>

                <nav className="side-nav__links">
                    {links.map(item => (
                        <Link
                            key={item.target}
                            to={item.target}
                            smooth={true}
                            duration={500}
                            offset={-70}
                            className="side-nav__link"
                            data-target={item.target}
                            onClick={() => handleNavClick(item.target)}
                        >
                            <span className="side-nav__icon">{item.icon}</span>
                            <span className="side-nav__label">{item.label}</span>
                            <span className="side-nav__tooltip">{item.label}</span>
                        </Link>
                    ))}
                </nav>

                <button type="button" className="side-nav__theme" onClick={toggleMode} aria-label="Toggle theme">
                    {mode ? <FiSun size={18} /> : <FiMoon size={18} />}
                </button>
            </div>
        </aside>
    );
}

export default Navbar;